import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Download, Mail, Paperclip, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel, StatusBadge } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import { DocEmailDialog } from "@/components/logistics/DcActions";
import { useFetch } from "@/hooks/useFetch";
import { api, downloadFile, errMsg } from "@/lib/api";
import { LARGE_FILE, uploadInParts } from "@/lib/chunkedUpload";
import { fmtDateTime } from "@/lib/format";

export const SR = "Service Report";

export async function uploadFiles(files, meta) {
  for (const file of files) {
    if (file.size > LARGE_FILE) { await uploadInParts({ file, meta, onProgress: () => {} }); continue; }
    const fd = new FormData();
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v ?? ""));
    fd.append("file", file);
    await api.post("/documents", fd);
  }
}

// Pick one or many files, or take photos; nothing is sent until onFiles uploads them
export function MultiPick({ files, setFiles, testid = "multi-pick" }) {
  const file = useRef(null);
  const cam = useRef(null);
  const add = (e) => { setFiles([...files, ...Array.from(e.target.files || [])]); e.target.value = ""; };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <input ref={file} type="file" multiple className="hidden" onChange={add} data-testid={`${testid}-file-input`} />
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={add} data-testid={`${testid}-camera-input`} />
      <Button type="button" size="sm" variant="outline" className="bg-white" onClick={() => file.current.click()} data-testid={`${testid}-choose`}><Paperclip className="h-4 w-4 mr-1" />Choose files</Button>
      <Button type="button" size="sm" variant="outline" className="bg-white" onClick={() => cam.current.click()} data-testid={`${testid}-photo`}><Camera className="h-4 w-4 mr-1" />Take photo</Button>
      <span className="text-xs text-slate-600" data-testid={`${testid}-count`}>{files.length ? `${files.length} file(s): ${files.map((f) => f.name).join(", ")}` : "No files chosen"}</span>
      {files.length > 0 && <button type="button" className="text-xs text-slate-500 hover:underline" onClick={() => setFiles([])}>Clear</button>}
    </div>
  );
}

export function SrUpload({ orderId, onDone, testid = "sr-upload" }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await uploadFiles(files, { entity_type: "order", entity_id: orderId, doc_type: SR, title: "" }); toast.success(`${files.length} service report file(s) uploaded`); setFiles([]); onDone?.(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <MultiPick files={files} setFiles={setFiles} testid={testid} />
      <Button size="sm" onClick={go} disabled={!files.length || busy} data-testid={`${testid}-btn`}><Upload className="h-4 w-4 mr-1" />{busy ? "Uploading…" : "Upload service report(s)"}</Button>
    </div>
  );
}

export const dl = (d) => downloadFile(`/documents/${d.id}/download`, d.stored_name).catch((e) => toast.error(errMsg(e)));

// LMS Lines tab: service report status, files, upload (many at once) and email
export function ServiceReportsPanel({ order, onChange }) {
  const { can } = useMe();
  const [k, setK] = useState(0);
  const [mail, setMail] = useState(false);
  const { data } = useFetch(`/documents?entity_type=order&entity_id=${order.id}&k=${k}`);
  const srs = (data || []).filter((d) => d.doc_type === SR);
  const done = () => { setK((x) => x + 1); onChange?.(); };
  const editable = can("write") && order.status !== "cancelled";
  return (
    <Panel title="Service reports" className="mt-4" testid="lms-service-reports"
      actions={<span className="flex items-center gap-2"><StatusBadge status={srs.length ? "received" : "pending"} testid="lms-sr-panel-status" />
        {srs.length > 0 && editable && <Button size="sm" variant="outline" onClick={() => setMail(true)} data-testid="lms-sr-email-btn"><Mail className="h-4 w-4 mr-1" />Email service report(s)</Button>}</span>}>
      {order.service_report_required === "No" && <p className="mb-2 text-xs text-slate-500">Service report not required on this LMS - you can still upload one.</p>}
      <div className="divide-y divide-slate-100 rounded border border-slate-200 text-xs mb-3" data-testid="lms-sr-list">
        {srs.map((d) => <div key={d.id} className="flex items-center gap-2 px-3 py-2"><span className="font-mono">{d.stored_name}</span><span className="text-slate-500">{d.uploaded_by?.name} · {fmtDateTime(d.uploaded_at)}</span>
          <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => dl(d)} data-testid={`lms-sr-download-${d.id}`}><Download className="h-4 w-4" /></Button></div>)}
        {!srs.length && <div className="px-3 py-2 text-slate-400">No service report uploaded yet</div>}
      </div>
      {editable && <SrUpload orderId={order.id} onDone={done} testid="lms-sr-upload" />}
      {mail && <DocEmailDialog url={`/service-reports/${order.id}/email`} kind="service_report" title={`Email service report(s) · ${order.order_no}`} onClose={() => setMail(false)} />}
    </Panel>
  );
}
